import { PropsWithTheme } from "../../../../providers/StoreThemeProvider"
import { Trans } from "@lingui/macro"
import { api } from "../../../../api"
import { colors } from "../../../../theme"
import { i18n } from "@lingui/core"
import styled from "@emotion/styled/macro"
import { useMemo } from "react"

type MercadoPagoPaymentMethodsProps = {
  mercadoPagoMethods: api.paymentMethods.Get.MPPaymentMethod[]
}

export function MercadoPagoPaymentMethods({
  mercadoPagoMethods,
}: MercadoPagoPaymentMethodsProps) {
  const mercadoPagoPaymentMethodTypes: {
    [key: string]: {
      title: import("react").ReactNode
      description: import("react").ReactNode
    }
  } = useMemo(
    () => ({
      credit_card: {
        title: <Trans>Tarjetas de crédito</Trans>,
        description: (
          <Trans>Tenemos cuotas sin interés con bancos seleccionados</Trans>
        ),
      },
      debit_card: {
        title: <Trans>Tarjetas de débito</Trans>,
        description: <Trans>Acreditación instantánea.</Trans>,
      },
      ...(i18n.locale === "pt-BR"
        ? {
            pix: {
              title: "Pix",
              description: "",
            },
          }
        : {}),
      other: {
        title: <Trans>Otros</Trans>,
        description: "",
      },
    }),
    []
  )

  const otherPaymentMethods = useMemo(() => {
    const knownTypes = Object.keys(mercadoPagoPaymentMethodTypes).filter(
      type => type !== "other"
    )

    return mercadoPagoMethods.filter(
      mpMethod => !knownTypes.includes(mpMethod.paymentTypeId)
    )
  }, [mercadoPagoMethods, mercadoPagoPaymentMethodTypes])

  return (
    <>
      {Object.keys(mercadoPagoPaymentMethodTypes).map(type =>
        type !== "other" ? (
          <PaymentMethodContainer>
            <Title>{mercadoPagoPaymentMethodTypes[type].title}</Title>
            <Description>
              {mercadoPagoPaymentMethodTypes[type].description}
            </Description>

            {mercadoPagoMethods
              .filter(method => method.paymentTypeId === type)
              .map(paymentMethod => {
                const { name, paymentTypeId, secureThumbnail } = paymentMethod

                return (
                  <Image
                    key={name + paymentTypeId}
                    src={secureThumbnail}
                    alt={name}
                  />
                )
              })}
          </PaymentMethodContainer>
        ) : (
          <PaymentMethodContainer>
            <Title>{mercadoPagoPaymentMethodTypes[type].title}</Title>
            <Description>
              {mercadoPagoPaymentMethodTypes[type].description}
            </Description>
            {otherPaymentMethods.map(paymentMethod => {
              const { name, paymentTypeId, secureThumbnail } = paymentMethod

              return (
                <Image
                  key={name + paymentTypeId}
                  src={secureThumbnail}
                  alt={name}
                />
              )
            })}
          </PaymentMethodContainer>
        )
      )}
    </>
  )
}

const Title = styled.h2<PropsWithTheme>(props => ({
  fontSize: "1.6rem",
  fontWeight: 600,
  fontStyle: "normal",
  color: props.theme.colors.texts,
  fontFamily: props.theme.typographies.texts,
  margin: 0,
}))

const Description = styled.p<PropsWithTheme>(props => ({
  fontSize: "1.4rem",
  fontWeight: "normal",
  marginBottom: "1.6rem",
  marginTop: "1.2rem",
  color: props.theme.colors.texts,
  fontFamily: props.theme.typographies.texts,
}))

const Image = styled.img({
  width: "5.2rem",
  height: "auto",
  objectFit: "cover",
  paddingRight: "1.2rem",
})

const PaymentMethodContainer = styled.div({
  borderBottom: `1px solid ${colors.greyMainLight}`,
  paddingBottom: "2rem",

  "&:last-child": {
    borderBottom: "none",
  },
})
